export const POPOUT_CHANNEL_NAME = 'dashboard-popouts';
export const HEARTBEAT_MS = 2000;
export const STALE_MS = 5000;
// A fresh popup boots the whole app (auth init, lazy chunk) before it can
// announce itself; do not prune it before then.
export const OPEN_GRACE_MS = 15000;
// A forced close asks the popup to close itself (only it can suppress its own
// unload prompt). If it never does — script error, unresponsive window — the
// dashboard closes the handle itself after this grace.
export const FORCE_CLOSE_FALLBACK_MS = 500;
// A hidden window's timers are throttled (down to one run a minute under
// Chromium's intensive throttling), so a live popup can miss STALE_MS by a
// wide margin. Before dropping a stale entry the dashboard posts `ping`;
// message events are not throttled, so a live popup replies within this
// grace and stays put.
export const PING_GRACE_MS = 2000;
// Same idea for an entry whose window handle is still open: silence plus an
// unanswered ping for this long means a crashed or frozen renderer, not
// throttling (message events are never throttled). The dashboard then closes
// the handle itself and restores the grid widget, so the dead window cannot
// come back later and unmount a live grid session.
export const HANDLE_PING_GRACE_MS = 60000;

export type PopoutMessage =
	| { type: 'opened'; widgetId: string; busy?: boolean; saving?: boolean }
	| { type: 'heartbeat'; widgetId: string; busy?: boolean; saving?: boolean }
	| { type: 'closed'; widgetId: string }
	| { type: 'ping' }
	| { type: 'close-request'; widgetId: string; force?: boolean }
	| { type: 'focus-request'; widgetId: string }
	| { type: 'weigh-order'; widgetId: string; orderId: number };

export type PopoutChannel = {
	post: (message: PopoutMessage) => void;
	subscribe: (handler: (message: PopoutMessage) => void) => () => void;
	close: () => void;
};

export const isPopoutSupported = () =>
	typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';

export const createPopoutChannel = (): PopoutChannel | null => {
	if (!isPopoutSupported()) return null;
	const channel = new BroadcastChannel(POPOUT_CHANNEL_NAME);
	return {
		post: (message) => channel.postMessage(message),
		subscribe: (handler) => {
			const listener = (ev: MessageEvent<PopoutMessage>) => handler(ev.data);
			channel.addEventListener('message', listener);
			return () => channel.removeEventListener('message', listener);
		},
		close: () => channel.close(),
	};
};

export const popoutWindowName = (id: string) => `sx-widget-${id}`;
export const popoutUrl = (id: string) =>
	`/dashboard/widget/${encodeURIComponent(id)}`;

/**
 * Fronts an already-open popup by its window name. `window.open('', name)`
 * returns the existing window without navigating it and counts as a user
 * gesture, unlike a `focus()` call from inside the popup. When no window of
 * that name is reachable the call creates a blank one; that is closed again
 * and `null` is returned so callers fall back to a `focus-request` message.
 */
export const frontPopoutWindow = (id: string): Window | null => {
	if (typeof window === 'undefined') return null;
	const handle = window.open('', popoutWindowName(id));
	if (!handle) return null;
	let href: string;
	try {
		href = handle.location.href;
	} catch {
		// Cross-origin: the named window navigated elsewhere. Not ours to
		// close, and not a popout any more either.
		return null;
	}
	if (href === 'about:blank') {
		handle.close();
		return null;
	}
	handle.focus();
	return handle;
};
